import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { ExplicitToggle } from '../../components/ui/ExplicitToggle';
import { Button } from '../../components/ui/Button';
import { EmojiButton } from '../../components/ui/EmojiPicker';
import { Card } from '../../components/ui/Card';
import { Select, Textarea } from '../../components/ui/form';
import { ProvenanceField, type Provenance } from '../tracks/provenance';
import { Field } from '../../components/ui/Field';
import { useAliases } from '../profile/aliases';
import { useSession } from '../../stores/session';
import { WaveformScan } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { UploadPreview, type PreviewState } from './UploadPreview';
import { ApiError, api } from '../../lib/api';
import { useEmojiComposer } from '../../lib/emoji';
import { pollUntil, uploadTrack, uploadArtwork } from '../../lib/upload';
import { ACCEPTED_AUDIO, contentTypeOf, describeFile, isAcceptedAudio } from './accepted';
import { useDocumentTitle } from '../../lib/title';

interface Genre {
  id: number;
  name: string;
  slug: string;
}

interface TrackStatus {
  id: number;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  failure_reason: string | null;
  /** The processed cover's URL, once its own job has run. */
  artwork: string | null;
}

type Phase = 'choosing' | 'uploading' | 'processing' | 'timeout' | 'failed' | 'done';

/**
 * The three screens the form is split across.
 *
 * Nine questions on one page is a wall, and the wall stands between somebody
 * and their music being on the site. Split by what kind of answer each wants:
 * the file, then what to call it and how it looks, then the two statements
 * the server will not accept an upload without. Every step is reachable at
 * any time from the strip at the top, so it is a form in three views rather
 * than a wizard with a gate on each door - the only gate is the button.
 */
type Step = 1 | 2 | 3;

const STEPS: { n: Step; label: string }[] = [
  { n: 1, label: 'The file' },
  { n: 2, label: 'About it' },
  { n: 3, label: 'Before it goes up' },
];

export function UploadPage() {
  useDocumentTitle('Upload');

  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [aliasId, setAliasId] = useState('');

  /*
   * The names this artist releases under, for the picker below. Empty for
   * almost everybody, which is why the picker is conditional.
   */
  const user = useSession((state) => state.user);
  const aliases = useAliases().data?.aliases ?? [];
  const [description, setDescription] = useState('');
  const [genreId, setGenreId] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'private'>('public');

  /*
   * Confirming the rights, per upload rather than once per account.
   *
   * Once per account would be the friendlier version and the wrong one: the
   * claim is about *this recording*, and the record the server keeps is stamped
   * on the track row. Somebody who cleared their own single in March has said
   * nothing about the remix they are uploading tonight.
   */
  const [rights, setRights] = useState(false);
  const [explicit, setExplicit] = useState(false);
  /*
   * No default, deliberately. A pre-selected answer to this question is an
   * answer the form gave on the artist's behalf, and the whole value of the
   * field is that somebody chose it.
   */
  const [provenance, setProvenance] = useState<Provenance | ''>('');

  /*
   * What has to be answered before the button does anything.
   *
   * `provenance` joins the rights box as a gate rather than being checked on
   * submit. The server requires it, and letting the button fire only to come
   * back with a field error is a worse way to learn that than a button that is
   * plainly not ready yet.
   */
  const ready = file !== null && rights && provenance !== '';

  /*
   * Tempo and key, kept as text.
   *
   * A number would make "empty" and "zero" the same state, and they are not:
   * nobody has said is a different answer from 0 BPM, and only one of them is
   * true of a track somebody skipped the field on.
   */

  /*
   * The cover, chosen here and uploaded afterwards.
   *
   * `POST /tracks/{id}/artwork/init` needs a track to attach to, so the image
   * genuinely cannot be sent before the audio has been. That is a fact about
   * the API and it was leaking into the form: you uploaded, waited, landed on
   * the track, and only then were offered a cover - which is the wrong order
   * for the person, whatever order the requests have to go in.
   *
   * So the file is held here and sent the moment the track exists. One form,
   * filled in whatever order suits, and the sequencing is this component's
   * problem rather than the artist's.
   */
  const [cover, setCover] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [coverProblem, setCoverProblem] = useState<string | null>(null);

  const [genres, setGenres] = useState<Genre[]>([]);
  const [step, setStep] = useState<Step>(1);
  const [phase, setPhase] = useState<Phase>('choosing');
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [trackId, setTrackId] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  const emoji = useEmojiComposer(description, setDescription);

  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  /*
   * A new step starts at the top of the card. Step two is the long one, and
   * leaving it from the bottom otherwise lands on the next step's footer
   * with its questions above the fold.
   */
  useEffect(() => {
    const top = cardRef.current?.getBoundingClientRect().top ?? 0;

    if (top < 0) {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [step]);

  /*
   * Whether this page is still on screen.
   *
   * The submit handler is a long async function that outlives the component:
   * it polls for up to five minutes, and everything after each `await` runs
   * whether or not anybody is still here. The page says you can leave while a
   * track is processing - and then, wherever you had gone, it navigated you to
   * the track. Read through a ref because those continuations hold a closure
   * over the render they started in, and a piece of state would be the value
   * from that render for ever.
   */
  const onPage = useRef(true);

  useEffect(() => {
    onPage.current = true;

    return () => {
      onPage.current = false;
    };
  }, []);

  useEffect(() => {
    api
      .get<{ items: Genre[] }>('/genres')
      .then((data) => setGenres(data.items))
      // A missing genre list is not a reason to block an upload: the field is
      // optional, so the select just stays at "Not sure yet".
      .catch(() => setGenres([]));
  }, []);

  /*
   * Warn before leaving mid-upload.
   *
   * Closing the tab during a multipart upload abandons parts in the bucket that
   * nothing will ever complete - and, more to the point, throws away however
   * long the upload had been running. Only registered while it would mean
   * something; a permanent handler would nag on the way out of an idle form.
   */
  useEffect(() => {
    if (phase !== 'uploading') return;

    const warn = (event: BeforeUnloadEvent) => event.preventDefault();

    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [phase]);

  function choose(picked: File | null | undefined) {
    setFileError(null);

    if (!picked) return;

    if (!isAcceptedAudio(picked)) {
      setFileError(`We can't process that format. Try ${ACCEPTED_AUDIO.label}.`);

      return;
    }

    setFile(picked);

    // The filename minus its extension, as a starting title. Almost always
    // close to right, and always easier to correct than to type from nothing.
    setTitle((current) => current || picked.name.replace(/\.[^.]+$/, ''));

    // The file was the question on this step; on to the next one, after a
    // beat so the chosen file is seen landing before the view moves.
    if (step === 1) {
      window.setTimeout(() => setStep(2), 420);
    }
  }

  /* `createObjectURL` pins the file until something revokes it, and the only
     something is this. */
  useEffect(
    () => () => {
      if (coverPreview !== null) URL.revokeObjectURL(coverPreview);
    },
    [coverPreview],
  );

  function chooseCover(picked: File | null): void {
    if (coverPreview !== null) URL.revokeObjectURL(coverPreview);

    setCover(picked);
    setCoverPreview(picked === null ? null : URL.createObjectURL(picked));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (!file || phase === 'uploading' || phase === 'processing') return;

    setError(null);
    setPercent(0);
    setPhase('uploading');

    const controller = new AbortController();
    abortRef.current = controller;

    let id: number;

    try {
      id = await uploadTrack(
        file,
        {
          title: title.trim() || file.name,
          content_type: contentTypeOf(file),
          size_bytes: file.size,
          genre_id: genreId ? Number(genreId) : null,
          // Absent and "my own name" are the same thing to the server.
          alias_id: aliasId ? Number(aliasId) : null,
          description: description.trim() || null,
          visibility,
          explicit,
          // Narrowed by `ready`, which the submit button is gated on.
          provenance: provenance as Provenance,
          rights_confirmed: rights,
        },
        { onProgress: setPercent, signal: controller.signal },
      );
    } catch (caught) {
      abortRef.current = null;

      // Cancelling is not failing. Back to the form with the fields intact, so
      // somebody who cancelled to fix a title has not lost the rest.
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        setPhase('choosing');
        setPercent(0);

        return;
      }

      setError(
        caught instanceof ApiError
          ? caught.message
          : caught instanceof Error
            ? caught.message
            : 'The upload failed.',
      );
      setPhase('failed');

      return;
    }

    abortRef.current = null;
    setTrackId(id);
    setPhase('processing');

    /*
     * The cover, now that there is something to attach it to.
     *
     * Deliberately not awaited into the failure path above: the track is
     * uploaded and safe at this point, and a cover that would not send is not a
     * reason to tell somebody their upload failed. It is reported quietly and
     * the track page can set one later.
     */
    let coverSent = false;

    if (cover !== null) {
      try {
        await uploadArtwork('tracks', id, cover);
        coverSent = true;
      } catch {
        setCoverProblem('The track uploaded. The cover did not. You can add one from the track.');
      }
    }

    /*
     * Poll until it is playable.
     *
     * Five minutes, because the job is two loudness passes, two renditions and
     * a waveform, and a long track is not quick. Timing out here is not a
     * failure - the work carries on regardless - so that path says where to find
     * the track rather than that something went wrong.
     *
     * This is the interim mechanism. Phase 4 replaces it with `track.ready` on
     * `private-user.{id}` (§8.1) and this becomes the fallback.
     */
    const settled = await pollUntil<TrackStatus>(
      () => api.get<TrackStatus>(`/tracks/${id}`),
      (track) => track.status === 'ready' || track.status === 'failed',
      {
        intervalMs: 2000,
        timeoutMs: 300_000,
        abandoned: () => !onPage.current,
      },
    );

    /*
     * Gone. The work carries on server-side and finishes without us, and
     * `TranscodeTrack` sends a notification when it does - so somebody who
     * wandered off is told in the one place that does not interrupt them.
     */
    if (!onPage.current) return;

    if (!settled) {
      setPhase('timeout');

      return;
    }

    const track = await api.get<TrackStatus>(`/tracks/${id}`).catch(() => null);

    // Checked again: that request is another chance to have left.
    if (!onPage.current) return;

    if (track?.status === 'failed') {
      setError(track.failure_reason ?? "We couldn't process that file.");
      setPhase('failed');

      return;
    }

    /*
     * The cover, a little longer.
     *
     * It is a separate job on the same queue as the transcode, queued after
     * it, so "ready" usually arrives a few seconds before the cover exists.
     * Landing on the track page in that gap shows an empty square for a cover
     * that was uploaded, which reads as "it did not take". A short wait covers
     * the gap; the cap is there so a cover job that fails cannot hold the
     * page, and the track page shows whatever is there at that point.
     */
    if (coverSent && track?.artwork === null) {
      await pollUntil<TrackStatus>(
        () => api.get<TrackStatus>(`/tracks/${id}`),
        (latest) => latest.artwork !== null,
        {
          intervalMs: 1500,
          timeoutMs: 45_000,
          abandoned: () => !onPage.current,
        },
      );

      if (!onPage.current) return;
    }

    // A check on the cover for a moment, so arriving on the track page reads
    // as the end of something rather than a jump cut.
    setPhase('done');
    await new Promise((resolve) => setTimeout(resolve, 900));

    if (!onPage.current) return;

    navigate(`/tracks/${id}`);
  }

  function reset() {
    setFile(null);
    setTitle('');
    setDescription('');
    setPercent(0);
    setError(null);
    setFileError(null);
    setTrackId(null);
    setPhase('choosing');
    setStep(1);
  }

  const busy = phase === 'uploading' || phase === 'processing' || phase === 'done';

  const genreName = genres.find((genre) => String(genre.id) === genreId)?.name ?? null;
  const aliasName = aliases.find((alias) => String(alias.id) === aliasId)?.name ?? null;

  const preview: PreviewState = {
    cover: coverPreview,
    title,
    artist: aliasName ?? user?.username ?? 'you',
    genre: genreName,
    visibility,
    explicit,
    provenance,
    description,
    hasFile: file !== null,
    fileLabel: file === null ? null : describeFile(file),
    phase,
    percent,
  };

  /*
   * What each step still wants, for the strip. A step is "done" when its
   * required answers are in, which is only ever the file and the two
   * statements - everything on step two is optional, so it is done the
   * moment it has been seen.
   */
  const complete: Record<Step, boolean> = {
    1: file !== null,
    2: file !== null && step > 2,
    3: rights && provenance !== '',
  };

  const waiting =
    phase === 'uploading' || phase === 'processing' || phase === 'done' || phase === 'timeout';

  return (
    <div className="mx-auto max-w-5xl px-6 py-12">
      <h1 className="text-2xl font-semibold tracking-tight text-ink">{copy['cta.upload']}</h1>
      <p className="mt-1.5 text-sm text-soft">
        {ACCEPTED_AUDIO.label}, up to 500MB. Upload the best quality you have. We make the streaming
        versions from it.
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start">
        {/* On a phone the preview sits above the form, as a row. */}
        <div className="lg:hidden">
          <UploadPreview state={preview} compact />
        </div>

        <div ref={cardRef} className="scroll-mt-24">
          <Card className="p-6">
            <form onSubmit={submit} className="flex flex-col gap-5" data-upload-form>
              {/*
              Always mounted, whatever step is showing, so "change the file"
              from any step and a test that sets it before the view has moved
              both have an input to talk to.
            */}
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED_AUDIO.accept}
                className="sr-only"
                data-file-input
                disabled={busy}
                onChange={(event) => choose(event.target.files?.[0])}
              />

              {/* ----------------------------------------------------------- */}
              {/* The strip                                                    */}
              {/* ----------------------------------------------------------- */}
              {!waiting && (
                <ol className="relative flex items-start" data-upload-steps>
                  {/* The line under the numbers, and the lit part of it that
                    grows as the steps are passed. */}
                  <span
                    aria-hidden
                    className="absolute left-[calc(16.7%)] right-[calc(16.7%)] top-3.5 h-px bg-line"
                  />
                  <span
                    aria-hidden
                    className="absolute left-[calc(16.7%)] top-3.5 h-px bg-accent transition-[width] duration-300 ease-out"
                    style={{ width: `${((step - 1) / 2) * 66.6}%` }}
                  />
                  {STEPS.map(({ n, label }) => {
                    const active = step === n;
                    const done = complete[n] && !active;

                    return (
                      <li key={n} className="relative flex flex-1 flex-col items-center gap-2">
                        <button
                          type="button"
                          data-step={n}
                          aria-current={active ? 'step' : undefined}
                          disabled={busy}
                          onClick={() => setStep(n)}
                          className={`flex h-7 w-7 items-center justify-center rounded-[var(--radius-pill)] border font-mono text-[11px] transition-all duration-200 ${
                            active
                              ? 'scale-110 border-accent bg-accent text-accent-ink'
                              : done
                                ? 'border-accent bg-surface text-accent'
                                : 'border-line-strong bg-surface text-faint hover:border-line-lit hover:text-ink'
                          }`}
                        >
                          {done ? (
                            <svg
                              viewBox="0 0 16 16"
                              className="h-3.5 w-3.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.2"
                            >
                              <path
                                d="m3.5 8.5 3 3 6-6.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            n
                          )}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => setStep(n)}
                          className={`text-[12px] transition-colors ${active ? 'font-medium text-ink' : 'text-faint hover:text-soft'}`}
                        >
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ol>
              )}

              {/* The chosen file, on every step after the first. */}
              {file !== null && step !== 1 && !waiting && (
                <div
                  className="flex items-center gap-3 rounded-[var(--radius-control)] border border-line bg-raised/50 px-3.5 py-2.5"
                  style={{ animation: 'gv-fade-in 200ms ease-out' }}
                >
                  <FileGlyph />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium text-ink" data-chosen-file>
                      {file.name}
                    </p>
                    <p className="meta-caps mt-0.5">{describeFile(file)}</p>
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => inputRef.current?.click()}
                    className="font-mono text-[11px] text-faint transition-colors hover:text-ink"
                  >
                    change
                  </button>
                </div>
              )}

              {/* ----------------------------------------------------------- */}
              {/* Step 1: the file                                             */}
              {/* ----------------------------------------------------------- */}
              {step === 1 && !waiting && (
                <div
                  key="step-1"
                  className="flex flex-col gap-5"
                  style={{
                    animation: 'gv-step-in 260ms cubic-bezier(0.22,1,0.36,1)',
                  }}
                >
                  <div
                    onDragOver={(event) => {
                      event.preventDefault();

                      if (!busy) setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setDragging(false);

                      if (!busy) choose(event.dataTransfer.files[0]);
                    }}
                    onClick={() => {
                      if (!busy && file === null) inputRef.current?.click();
                    }}
                    className={[
                      'relative rounded-[var(--radius-card)] px-6 py-12 text-center transition-[transform,background-color] duration-200',
                      dragging ? 'scale-[1.015] bg-raised' : '',
                      file === null ? 'cursor-pointer' : '',
                    ].join(' ')}
                    data-dropzone
                  >
                    {/* The edge: an SVG so the dashes can move. */}
                    <svg aria-hidden className="pointer-events-none absolute inset-0 h-full w-full">
                      <rect
                        x="0.5"
                        y="0.5"
                        width="calc(100% - 1px)"
                        height="calc(100% - 1px)"
                        rx="14"
                        fill="none"
                        stroke={
                          dragging || file !== null
                            ? 'var(--color-accent)'
                            : 'var(--color-line-strong)'
                        }
                        strokeWidth="1"
                        strokeDasharray={file === null ? '8 4' : '0'}
                        style={{
                          animation: file === null ? 'gv-march 1.4s linear infinite' : undefined,
                          transition: 'stroke 200ms',
                        }}
                      />
                    </svg>

                    {file ? (
                      <div
                        style={{
                          animation: 'gv-pop 320ms cubic-bezier(0.22,1,0.36,1)',
                        }}
                      >
                        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-[var(--radius-pill)] bg-accent text-accent-ink">
                          <svg
                            viewBox="0 0 20 20"
                            className="h-6 w-6"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                          >
                            <path
                              d="m4.5 10.5 3.6 3.6L15.5 6.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <p className="mt-4 text-sm font-medium text-ink" data-chosen-file>
                          {file.name}
                        </p>
                        <p className="meta-caps mt-1.5">{describeFile(file)}</p>

                        {!busy && (
                          <Button
                            variant="ghost"
                            className="mt-3"
                            onClick={() => inputRef.current?.click()}
                          >
                            Choose a different file
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div>
                        <span
                          className={`mx-auto flex h-12 w-12 items-center justify-center rounded-[var(--radius-pill)] border border-line-strong text-soft transition-transform duration-200 ${
                            dragging ? 'scale-110 border-accent text-accent' : ''
                          }`}
                        >
                          <svg
                            viewBox="0 0 20 20"
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                          >
                            <path
                              d="M10 13V4m0 0-3.5 3.5M10 4l3.5 3.5M4 13.5v1A1.5 1.5 0 0 0 5.5 16h9a1.5 1.5 0 0 0 1.5-1.5v-1"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <p className="mt-4 text-sm text-ink">
                          {dragging ? 'Let go' : 'Drag your track here'}
                        </p>
                        <p className="mt-1 text-xs text-faint">or</p>
                        <Button
                          variant="secondary"
                          className="mt-3"
                          onClick={(event) => {
                            event.stopPropagation();
                            inputRef.current?.click();
                          }}
                        >
                          Browse
                        </Button>
                      </div>
                    )}
                  </div>

                  {fileError && <Alert tone="error">{fileError}</Alert>}

                  <StepNav
                    next={file !== null ? () => setStep(2) : null}
                    nextLabel="Next: about it"
                  />
                </div>
              )}

              {/* ----------------------------------------------------------- */}
              {/* Step 2: about it                                             */}
              {/* ----------------------------------------------------------- */}
              {step === 2 && !waiting && (
                <div
                  key="step-2"
                  className="flex flex-col gap-5"
                  style={{
                    animation: 'gv-step-in 260ms cubic-bezier(0.22,1,0.36,1)',
                  }}
                >
                  <Field
                    label="Title"
                    name="title"
                    value={title}
                    maxLength={120}
                    disabled={busy}
                    required
                    autoFocus
                    onChange={(event) => setTitle(event.target.value)}
                  />

                  {/*
                  Which name this goes out under, and only when there is a choice.

                  An artist with no aliases has one answer, and a select with one
                  option in it is a question that wastes everybody's time. It appears
                  the moment they add a second name in settings.
                */}
                  {aliases.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="track-alias" className="text-sm font-medium text-soft">
                        Release as
                      </label>
                      <Select
                        id="track-alias"
                        value={aliasId}
                        disabled={busy}
                        placeholder={user?.display_name ?? 'Your own name'}
                        options={aliases.map((alias) => ({
                          value: String(alias.id),
                          label: alias.name,
                        }))}
                        onChange={setAliasId}
                      />
                      <p className="text-[13px] text-faint">
                        Leave it as {user?.display_name ?? 'your own name'} unless this belongs to
                        one of your other projects.
                      </p>
                    </div>
                  )}

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="track-genre" className="text-sm font-medium text-soft">
                        Genre
                      </label>
                      <Select
                        id="track-genre"
                        value={genreId}
                        disabled={busy}
                        placeholder="Not sure yet"
                        options={genres.map((genre) => ({
                          value: String(genre.id),
                          label: genre.name,
                        }))}
                        onChange={setGenreId}
                      />
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="track-visibility" className="text-sm font-medium text-soft">
                        Who can hear it
                      </label>
                      <Select
                        id="track-visibility"
                        value={visibility}
                        disabled={busy}
                        data-visibility
                        options={[
                          {
                            value: 'public',
                            label: 'Public',
                            hint: 'anybody can find it',
                          },
                          {
                            value: 'unlisted',
                            label: 'Unlisted',
                            hint: 'only people with the link',
                          },
                          {
                            value: 'private',
                            label: 'Private',
                            hint: 'only you',
                          },
                        ]}
                        onChange={(next) =>
                          setVisibility(next as 'public' | 'unlisted' | 'private')
                        }
                      />
                      <p className="text-xs text-faint">
                        {/*
                        Said plainly, because the difference is the whole reason the
                        media gate exists (§4.4) and "unlisted" means different things
                        on different services.
                      */}
                        {visibility === 'public'
                          ? 'Shows up on your profile and in discovery.'
                          : visibility === 'unlisted'
                            ? 'Hidden from your profile. Anyone you send the link to can play it.'
                            : 'Nobody but you can play it.'}
                      </p>
                    </div>
                  </div>

                  {/*
                  Tempo and key are not asked for here.

                  They were two fields almost nobody filled in, on the one form that
                  stands between somebody and their music being on the site. They are
                  measured from the audio once it is up and offered on the track,
                  where the person deciding is somebody who has decided they matter.
                */}

                  {/*
                  Chosen here, sent after the track exists.

                  It used to be unavailable until the upload had finished and you had
                  landed on the track page - the API's ordering showing through the
                  form. Nothing about picking a file needs the track to exist; only
                  sending it does, and that is this component's problem.
                */}
                  <div className="flex items-center gap-4">
                    <label
                      htmlFor="track-cover"
                      className={`flex h-20 w-20 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-[var(--radius-control)] border border-line bg-raised transition-colors ${
                        busy ? 'pointer-events-none opacity-60' : 'hover:border-line-strong'
                      }`}
                    >
                      {coverPreview !== null ? (
                        <img src={coverPreview} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <span className="meta-caps text-faint">Cover</span>
                      )}
                    </label>

                    <input
                      id="track-cover"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      disabled={busy}
                      data-cover-input
                      className="hidden"
                      onChange={(event) => chooseCover(event.target.files?.[0] ?? null)}
                    />

                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="text-sm font-medium text-soft">Cover art</span>
                      <span className="text-xs leading-relaxed text-faint">
                        Optional, and you can add one later. Square looks best.
                      </span>

                      {cover !== null && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => chooseCover(null)}
                          className="mt-0.5 self-start font-mono text-[11px] text-faint transition-colors hover:text-hot-text"
                        >
                          remove
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="track-description" className="text-sm font-medium text-soft">
                      Description
                    </label>
                    <Textarea
                      id="track-description"
                      ref={emoji.ref as React.RefObject<HTMLTextAreaElement>}
                      className="min-h-24"
                      value={description}
                      maxLength={2000}
                      disabled={busy}
                      placeholder="Credits, gear, what it's about. Optional."
                      onChange={(event) => setDescription(event.target.value)}
                    />
                    <div className="-mt-0.5 flex">
                      <EmojiButton onSelect={emoji.insert} direction="down" align="left" />
                    </div>
                  </div>

                  <StepNav
                    back={() => setStep(1)}
                    next={() => setStep(3)}
                    nextLabel="Next: before it goes up"
                  />
                </div>
              )}

              {/* ----------------------------------------------------------- */}
              {/* Step 3: before it goes up                                    */}
              {/* ----------------------------------------------------------- */}
              {step === 3 && !waiting && (
                <div
                  key="step-3"
                  className="flex flex-col gap-5"
                  style={{
                    animation: 'gv-step-in 260ms cubic-bezier(0.22,1,0.36,1)',
                  }}
                >
                  {/* Two statements about the work, then the one about the rights. */}
                  <ProvenanceField
                    id="track-provenance"
                    value={provenance}
                    disabled={busy}
                    onChange={setProvenance}
                  />

                  <ExplicitToggle value={explicit} onChange={setExplicit} disabled={busy} />

                  {/*
                  The rights, per upload.

                  A statement about this recording, and the button will not go
                  without it.
                */}
                  <label className="flex cursor-pointer items-start gap-3 text-[13px] leading-relaxed text-soft">
                    <input
                      type="checkbox"
                      checked={rights}
                      data-confirm-rights
                      onChange={(event) => setRights(event.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--color-accent)]"
                    />
                    <span>
                      I have the right to upload this recording, and I have read{' '}
                      <Link
                        to="/artist-rights"
                        target="_blank"
                        className="text-accent hover:underline"
                      >
                        Your music and your rights
                      </Link>
                      .
                    </span>
                  </label>

                  {error && <Alert tone="error">{error}</Alert>}
                  {coverProblem !== null && <Alert tone="info">{coverProblem}</Alert>}

                  {file === null && (
                    <p className="text-[13px] text-faint">
                      There is no file yet.{' '}
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="text-accent hover:underline"
                      >
                        Pick one
                      </button>{' '}
                      and come back.
                    </p>
                  )}

                  <div className="flex items-center justify-between gap-3 border-t border-line pt-5">
                    <Button variant="ghost" onClick={() => setStep(2)} data-step-back>
                      Back
                    </Button>
                    {phase === 'failed' ? (
                      <Button onClick={reset}>Start over</Button>
                    ) : (
                      <Button type="submit" disabled={!ready}>
                        {copy['cta.upload']}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* ----------------------------------------------------------- */}
              {/* The waits                                                    */}
              {/* ----------------------------------------------------------- */}
              {phase === 'uploading' && (
                <div
                  key="uploading"
                  data-upload-progress
                  style={{ animation: 'gv-step-in 260ms ease-out' }}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-ink">Uploading {file?.name}</span>
                    <span className="meta-caps tabular-nums" data-upload-percent>
                      {percent}%
                    </span>
                  </div>
                  <div
                    className="mt-2 h-1.5 overflow-hidden rounded-[var(--radius-pill)] bg-line"
                    role="progressbar"
                    aria-label="Upload progress"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percent}
                  >
                    <div
                      className="h-full rounded-[var(--radius-pill)] bg-accent transition-[width] duration-200"
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <div className="mt-5 flex justify-end">
                    <Button variant="secondary" onClick={() => abortRef.current?.abort()}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {phase === 'processing' && (
                <div
                  key="processing"
                  className="flex items-start gap-4"
                  data-upload-processing
                  style={{ animation: 'gv-step-in 260ms ease-out' }}
                >
                  {/*
                  The scanning waveform, not a spinner. This wait is the encoder
                  reading the file, and a lit segment travelling along a wave says
                  that - it is the difference between "the site is busy" and "the
                  site is working on my track", which is what somebody standing
                  over a two-minute transcode wants to know.
                */}
                  <WaveformScan className="mt-0.5 w-24 shrink-0" />
                  <div>
                    <p className="text-sm text-ink">
                      Your file is up. We're building the streaming versions.
                    </p>
                    <p className="mt-1 text-xs text-faint">
                      Roughly as long as the track itself. You can leave this page. It'll be on your
                      profile when it's done.
                    </p>
                  </div>
                </div>
              )}

              {phase === 'done' && (
                <p
                  key="done"
                  className="text-sm text-ink"
                  data-upload-done
                  style={{ animation: 'gv-step-in 260ms ease-out' }}
                >
                  It's up. Taking you there.
                </p>
              )}

              {phase === 'timeout' && trackId !== null && (
                <div className="flex flex-col gap-4">
                  <Alert tone="info">
                    Still processing. It's taking longer than usual, but it hasn't failed. It'll
                    appear on your profile when it's ready.
                  </Alert>
                  <div>
                    <Button onClick={reset}>Upload another</Button>
                  </div>
                </div>
              )}
            </form>
          </Card>
        </div>

        <div className="hidden lg:sticky lg:top-24 lg:block">
          <UploadPreview state={preview} />
        </div>
      </div>
    </div>
  );
}

/** Back on the left, next on the right; either can be absent. */
function StepNav({
  back,
  next,
  nextLabel,
}: {
  back?: () => void;
  next: (() => void) | null;
  nextLabel: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-line pt-5">
      {back ? (
        <Button variant="ghost" onClick={back} data-step-back>
          Back
        </Button>
      ) : (
        <span />
      )}
      <Button
        variant="secondary"
        disabled={next === null}
        onClick={next ?? undefined}
        data-step-next
      >
        {nextLabel}
      </Button>
    </div>
  );
}

function FileGlyph() {
  return (
    <span
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-accent/12 text-accent"
      aria-hidden
    >
      <svg
        viewBox="0 0 20 20"
        className="h-4 w-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      >
        <path
          d="M8 15.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Zm0 0V5l8-2v10m0 0a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0Z"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
